/**
 * Progress data model plus the pure mastery and diagnostics logic.
 *
 * Kept free of storage and DOM concerns so it can be unit-tested directly. The
 * repository persists these shapes; the screens render derived views.
 *
 * Design commitments:
 *  - Mastery is multi-factor and gated by a minimum sample. One good quiz does
 *    not equal mastery; we avoid false precision by reporting coarse states.
 *  - Diagnostics report CORRELATIONS over tagged subskills ("X of last Y"),
 *    never a verdict about what a student does or does not understand.
 */

import type { TopicId } from "../curriculum/curriculum";
import type { SubskillId } from "../curriculum/subskills";

export const PROGRESS_SCHEMA_VERSION = 1;

/** A rolling record of recent outcomes (most recent last), capped in length. */
export interface Window {
  /** 1 = correct on first attempt with no help; 0 otherwise. */
  readonly firstTry: number[];
  /** 1 = eventually correct (any attempts/help); 0 otherwise. */
  readonly eventual: number[];
}

export interface TopicProgress {
  attempts: number;
  window: Window;
  /** Distinct problem families seen recently (variety signal). */
  recentFamilies: string[];
  lastPracticedAt: number | null;
  hintReliance: number[]; // 1 if help was used on a recent question, else 0
}

export interface SubskillProgress {
  correct: number[];
  // Aligned with correct[]: same length, most recent last.
}

export interface SessionRecord {
  kind: "practice" | "quiz";
  at: number;
  topic: TopicId | "mixed";
  total: number;
  correct: number;
  firstTry: number;
}

/**
 * A dated snapshot of how one subskill did in a single session. Unlike the
 * rolling `subskills` window (current state only), these carry a timestamp so
 * we can show growth over time. Additive and optional so older saved data
 * (which lacks it) still loads.
 */
export interface SubskillSnapshot {
  at: number;
  subskill: SubskillId;
  correct: number;
  total: number;
}

export interface ProgressData {
  schemaVersion: number;
  topics: Partial<Record<TopicId, TopicProgress>>;
  subskills: Partial<Record<SubskillId, SubskillProgress>>;
  sessions: SessionRecord[];
  /** Dated per-subskill accuracy snapshots for the growth view. */
  subskillHistory?: SubskillSnapshot[];
}

export function emptyProgress(): ProgressData {
  return {
    schemaVersion: PROGRESS_SCHEMA_VERSION,
    topics: {},
    subskills: {},
    sessions: [],
    subskillHistory: [],
  };
}

const WINDOW_CAP = 20;
const SUBSKILL_CAP = 20;
const SESSION_CAP = 100;

export function pushCapped(arr: number[], v: number, cap: number): void {
  arr.push(v);
  while (arr.length > cap) arr.shift();
}

export function ensureTopic(data: ProgressData, topic: TopicId): TopicProgress {
  let t = data.topics[topic];
  if (!t) {
    t = {
      attempts: 0,
      window: { firstTry: [], eventual: [] },
      recentFamilies: [],
      lastPracticedAt: null,
      hintReliance: [],
    };
    data.topics[topic] = t;
  }
  return t;
}

export function ensureSubskill(
  data: ProgressData,
  sk: SubskillId,
): SubskillProgress {
  let s = data.subskills[sk];
  if (!s) {
    s = { correct: [] };
    data.subskills[sk] = s;
  }
  return s;
}

/** Record one answered question against a topic. */
export function recordTopicOutcome(
  data: ProgressData,
  topic: TopicId,
  opts: {
    firstTry: boolean;
    eventual: boolean;
    family: string;
    usedHelp: boolean;
    at: number;
  },
): void {
  const t = ensureTopic(data, topic);
  t.attempts++;
  pushCapped(t.window.firstTry, opts.firstTry ? 1 : 0, WINDOW_CAP);
  pushCapped(t.window.eventual, opts.eventual ? 1 : 0, WINDOW_CAP);
  pushCapped(t.hintReliance, opts.usedHelp ? 1 : 0, WINDOW_CAP);
  t.lastPracticedAt = opts.at;
  t.recentFamilies.push(opts.family);
  while (t.recentFamilies.length > WINDOW_CAP) t.recentFamilies.shift();
}

/** Record one answered question against a subskill. */
export function recordSubskillOutcome(
  data: ProgressData,
  sk: SubskillId,
  correct: boolean,
): void {
  const s = ensureSubskill(data, sk);
  pushCapped(s.correct, correct ? 1 : 0, SUBSKILL_CAP);
}

export function addSession(data: ProgressData, record: SessionRecord): void {
  data.sessions.push(record);
  while (data.sessions.length > SESSION_CAP) data.sessions.shift();
}

const HISTORY_CAP = 400;

/**
 * Record dated per-subskill accuracy for one session. `perSubskill` maps a
 * subskill to its {correct,total} tally within that session. Snapshots with no
 * questions are skipped.
 */
export function addSubskillSnapshots(
  data: ProgressData,
  at: number,
  perSubskill: ReadonlyMap<SubskillId, { correct: number; total: number }>,
): void {
  if (!data.subskillHistory) data.subskillHistory = [];
  for (const [subskill, tally] of perSubskill) {
    if (tally.total <= 0) continue;
    data.subskillHistory.push({
      at,
      subskill,
      correct: tally.correct,
      total: tally.total,
    });
  }
  while (data.subskillHistory.length > HISTORY_CAP) data.subskillHistory.shift();
}

// --- Mastery -------------------------------------------------------------

export type MasteryState =
  | "not-started"
  | "learning"
  | "proficient"
  | "strong";

export const MASTERY_LABEL: Record<MasteryState, string> = {
  "not-started": "Not Started",
  learning: "Learning",
  proficient: "Proficient",
  strong: "Strong",
};

/** Minimum answered questions before we assign anything above "learning". */
const MASTERY_MIN_SAMPLE = 8;

function mean(arr: readonly number[]): number {
  if (arr.length === 0) return 0;
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

/**
 * Coarse, multi-factor mastery. Weighs recent first-attempt accuracy most,
 * discounts heavy hint reliance, and requires variety + a minimum sample for
 * the top state. Deliberately avoids a precise percentage.
 */
export function masteryFor(t: TopicProgress | undefined): MasteryState {
  if (!t || t.attempts === 0) return "not-started";
  const n = t.window.firstTry.length;
  if (n < MASTERY_MIN_SAMPLE) return "learning";

  const firstTry = mean(t.window.firstTry);
  const eventual = mean(t.window.eventual);
  const helpRate = mean(t.hintReliance);
  const variety = new Set(t.recentFamilies).size;

  if (firstTry >= 0.85 && helpRate <= 0.25 && variety >= 3) {
    return "strong";
  }
  if (firstTry >= 0.7 || (eventual >= 0.85 && firstTry >= 0.55)) {
    return "proficient";
  }
  return "learning";
}

// --- Diagnostics ---------------------------------------------------------

/** Minimum recent samples before a subskill is surfaced as a concern. */
const DIAGNOSTIC_MIN_SAMPLE = 4;

export interface SubskillConcern {
  readonly subskill: SubskillId;
  readonly recentCorrect: number;
  readonly recentTotal: number;
}

/**
 * Surface subskills where recent accuracy is low, worded as a correlation.
 * Returns concerns sorted by weakest first. Never asserts causation.
 */
export function subskillConcerns(
  data: ProgressData,
  opts: { maxResults?: number; accuracyThreshold?: number } = {},
): SubskillConcern[] {
  const threshold = opts.accuracyThreshold ?? 0.7;
  const max = opts.maxResults ?? 3;
  const concerns: SubskillConcern[] = [];

  for (const [sk, prog] of Object.entries(data.subskills)) {
    if (!prog) continue;
    const total = prog.correct.length;
    if (total < DIAGNOSTIC_MIN_SAMPLE) continue;
    const correct = prog.correct.reduce((s, v) => s + v, 0);
    if (correct / total < threshold) {
      concerns.push({
        subskill: sk as SubskillId,
        recentCorrect: correct,
        recentTotal: total,
      });
    }
  }

  concerns.sort(
    (a, b) =>
      a.recentCorrect / a.recentTotal - b.recentCorrect / b.recentTotal,
  );
  return concerns.slice(0, max);
}

// --- Skill growth over time ---------------------------------------------

export interface TrendPoint {
  readonly at: number;
  /** Accuracy for this snapshot, 0..1. */
  readonly accuracy: number;
}

export interface SubskillTrend {
  readonly subskill: SubskillId;
  readonly points: readonly TrendPoint[];
  /** Cumulative questions across the snapshots. */
  readonly totalQuestions: number;
  /**
   * A plain, correlation-only direction over the window: "improving",
   * "steady", or "dipping". Compares the average accuracy of the earlier half
   * of the points to the later half; never claims causation or understanding.
   */
  readonly direction: "improving" | "steady" | "dipping";
}

/** Minimum dated snapshots before a subskill trend is worth showing. */
const TREND_MIN_POINTS = 2;

function avg(points: readonly TrendPoint[]): number {
  if (points.length === 0) return 0;
  return points.reduce((s, p) => s + p.accuracy, 0) / points.length;
}

/** Build ordered trends for every subskill with enough dated history. */
export function subskillTrends(data: ProgressData): SubskillTrend[] {
  const history = data.subskillHistory ?? [];
  const bySkill = new Map<SubskillId, SubskillSnapshot[]>();
  for (const snap of history) {
    const list = bySkill.get(snap.subskill) ?? [];
    list.push(snap);
    bySkill.set(snap.subskill, list);
  }

  const trends: SubskillTrend[] = [];
  for (const [subskill, snaps] of bySkill) {
    if (snaps.length < TREND_MIN_POINTS) continue;
    const ordered = [...snaps].sort((a, b) => a.at - b.at);
    const points: TrendPoint[] = ordered.map((s) => ({
      at: s.at,
      accuracy: s.total > 0 ? s.correct / s.total : 0,
    }));
    const totalQuestions = ordered.reduce((s, p) => s + p.total, 0);

    const half = Math.floor(points.length / 2);
    const earlier = avg(points.slice(0, half || 1));
    const later = avg(points.slice(half));
    const delta = later - earlier;
    const direction =
      delta > 0.1 ? "improving" : delta < -0.1 ? "dipping" : "steady";

    trends.push({ subskill, points, totalQuestions, direction });
  }
  trends.sort((a, b) => b.totalQuestions - a.totalQuestions);
  return trends;
}

// --- Weekly summary ------------------------------------------------------

export interface WeeklySummary {
  questionsPracticed: number;
  firstTryAccuracy: number | null; // null when nothing practiced
  quizzesCompleted: number;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export function weeklySummary(
  data: ProgressData,
  now: number,
): WeeklySummary {
  const cutoff = now - WEEK_MS;
  const recent = data.sessions.filter((s) => s.at >= cutoff);
  const questions = recent.reduce((s, r) => s + r.total, 0);
  const firstTry = recent.reduce((s, r) => s + r.firstTry, 0);
  const quizzes = recent.filter((r) => r.kind === "quiz").length;
  return {
    questionsPracticed: questions,
    firstTryAccuracy: questions > 0 ? firstTry / questions : null,
    quizzesCompleted: quizzes,
  };
}
